// Copyright (c) 2020 WiseTime. All rights reserved.

import React from "react"
import styled, { css, SimpleInterpolation } from "styled-components/macro"

const Wrapper = styled.span<{ position: Position }>`
  cursor: default;
  white-space: nowrap;
  color: ${({ theme }): string => theme.colors.white};
  font-size: 1.1rem;
  font-weight: 400;
  line-height: normal;
  background-color: ${({ theme }): string => theme.colors.indigo};
  padding: .5rem 1rem .7rem;
  border-radius: 3px;
  position: absolute;
  z-index: 10;
  vertical-align: top;
  display: none;
  
  &:after {
    content: '';
    border-style: solid;
    position: absolute;
  }
  
  &::selection {
    color: ${({ theme }): string => theme.colors.black};
    background-color: ${({ theme }): string => theme.colors.white};
  }

  ${({ position, theme }): SimpleInterpolation => {
    if (position === "top-right") {
      return css`
        top: -.5rem;
        right: 0;
        transform: translateY(-100%);
        &:after {
          border-width: .3rem .3rem 0 .3rem;
          border-color: ${theme.colors.indigo} transparent transparent transparent;
          right: 1rem;
          bottom: -.3rem;
        }
      `
    }
    if (position === "top-center") {
      return css`
        top: -.5rem;
        left: 50%;
        transform: translate(-50%, -100%);
      
        &:after {
          border-width: .3rem .3rem 0 .3rem;
          border-color: ${theme.colors.indigo} transparent transparent transparent;
          left: 50%;
          bottom: -.3rem;
          margin-left: -3px;
        }
      `
    }
    if (position === "bottom-right") {
      return css`
        transform: translateY(100%);
        right: 0;
        bottom: -.5rem;
      
        &:after {
          right: 1rem;
          top: -.3rem;
          border-width: 0 .3rem .3rem .3rem;
          border-color: transparent transparent ${theme.colors.indigo} transparent;
        }
      `
    }
    if (position === "bottom-left") {
      return css`
        transform: translateY(100%);
        left: 0;
        bottom: -.5rem;
      
        &:after {
          left: 1rem;
          top: -.3rem;
          border-width: 0 .3rem .3rem .3rem;
          border-color: transparent transparent ${theme.colors.indigo} transparent;
        }
      `
    }
    if (position === "bottom-center") {
      return css`
        bottom: -.5rem;
        left: 50%;
        transform: translate(-50%, 100%);
      
        &:after {
          top: -.3rem;
          left: 50%;
          margin-left: -3px;
          border-width: 0 .3rem .3rem .3rem;
          border-color: transparent transparent ${theme.colors.indigo} transparent;
        }
      `
    }
    return css`
      top: -.5rem;
      left: 0;
      transform: translateY(-100%);
      &:after {
        border-width: .3rem .3rem 0 .3rem;
        border-color: ${theme.colors.indigo} transparent transparent transparent;
        left: 1rem;
        bottom: -.3rem;
      }
    `
  }}
`

type Position = "top-left"
  | "top-right"
  | "top-center"
  | "bottom-right"
  | "bottom-left"
  | "bottom-center"

type Props = {
  text: string | React.ReactNode,
  className?: string,
  position?: Position,
}

const Tooltip = ({
  text,
  className,
  position = "top-left",
}: Props): React.ReactElement<Props> => {
  return (
    <Wrapper
      onClick={(event: React.MouseEvent<HTMLLIElement>): void => event.stopPropagation()}
      className={className}
      position={position}
      data-tooltip
    >
      {text}
    </Wrapper>
  )
}

export default Tooltip
